import React, { useEffect, useState } from "react";
import { MapPin, Phone, Tag } from "lucide-react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

interface Kurum {
  id: string;
  ad: string;
  kategori: string;
  avantaj: string;
  adres: string;
  telefon: string;
  aciklama?: string;
}

// Kategori → Font Awesome ikon (mobil Keşfet ile uyumlu kategoriler).
function kategoriIkon(kategori: string): string {
  const k = (kategori ?? "").toLocaleLowerCase("tr-TR");
  if (k.includes("restoran") || k.includes("yemek") || k.includes("kafe")) return "fa-utensils";
  if (k.includes("sağlık") || k.includes("saglik") || k.includes("hastane") || k.includes("eczane")) return "fa-kit-medical";
  if (k.includes("spor") || k.includes("fitness")) return "fa-dumbbell";
  if (k.includes("eğitim") || k.includes("egitim") || k.includes("kurs") || k.includes("okul")) return "fa-graduation-cap";
  if (k.includes("market") || k.includes("alışveriş") || k.includes("alisveris")) return "fa-cart-shopping";
  return "fa-store";
}

const AnlasmaliKurumlarPage: React.FC = () => {
  const [kurumlar, setKurumlar] = useState<Kurum[]>([]);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);
  const [active, setActive] = useState("Tümü");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setHata(false);
      try {
        // Mobil Keşfet ile aynı: orderBy yok, koleksiyon olduğu gibi okunur.
        const snap = await getDocs(collection(db, "anlasmaliKurumlar"));
        const list: Kurum[] = snap.docs.map((d) => {
          const data = d.data() as any;
          return {
            id: d.id,
            ad: data.ad ?? "",
            kategori: (data.kategori ?? "").trim() || "Diğer",
            avantaj: data.avantaj ?? "",
            adres: data.adres ?? "",
            telefon: data.telefon ?? "",
            aciklama: data.aciklama ?? "",
          };
        });
        if (!cancelled) setKurumlar(list);
      } catch (e) {
        console.error("Anlaşmalı kurumlar okunamadı:", e);
        if (!cancelled) setHata(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filtre: "Tümü" + verideki mevcut kategoriler.
  const kategoriler = Array.from(new Set(kurumlar.map((k) => k.kategori))).sort();
  const tabs = ["Tümü", ...kategoriler];
  const filtered = active === "Tümü" ? kurumlar : kurumlar.filter((k) => k.kategori === active);

  return (
    <div className="min-h-screen bg-bg-page pt-24">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0F172A] to-brand-bordo-dark py-16 text-white">
        <div className="w-full px-6 xl:px-16 text-center">
          <div className="text-brand-gold text-sm font-semibold uppercase tracking-wider mb-2">
            Üyelere Özel
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Anlaşmalı Kurumlar ve Avantajlar</h1>
          <p className="text-white/70 max-w-xl mx-auto">
            Üyelerimize özel indirim ve ayrıcalıklar sunan anlaşmalı kurumlarımız.
          </p>
        </div>
      </div>

      <div className="w-full px-6 xl:px-16 py-12">
        {loading ? (
          <div className="py-20 text-center text-text-soft">
            <i className="fa-solid fa-circle-notch fa-spin text-3xl text-brand-bordo" />
            <p className="mt-3 text-sm">Kurumlar yükleniyor...</p>
          </div>
        ) : hata ? (
          <div className="py-20 text-center text-red-600">
            Kurumlar yüklenemedi. Lütfen sayfayı yenileyin.
          </div>
        ) : kurumlar.length === 0 ? (
          <div className="py-20 text-center text-text-muted">
            <i className="fa-solid fa-store text-4xl opacity-40 mb-3" />
            <p className="font-medium">Henüz anlaşmalı kurum eklenmemiş.</p>
            <p className="text-sm text-text-soft mt-1">Yeni anlaşmalar yakında burada olacak.</p>
          </div>
        ) : (
          <>
            {/* Kategori filtresi */}
            {tabs.length > 1 && (
              <div className="flex gap-2 flex-wrap mb-8">
                {tabs.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActive(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      active === cat
                        ? "bg-brand-bordo text-white"
                        : "bg-bg-surface border border-border-light text-text-muted hover:text-text-primary"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* Kurum kartları */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((k) => (
                <div key={k.id} className="card flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full bg-brand-bordo/10 flex items-center justify-center flex-shrink-0">
                      <i className={`fa-solid ${kategoriIkon(k.kategori)} text-brand-bordo`} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-text-primary leading-tight">{k.ad}</h3>
                      <span className="text-xs text-text-soft">{k.kategori}</span>
                    </div>
                  </div>

                  {k.avantaj ? (
                    <div className="flex items-center gap-2 mb-3">
                      <Tag size={14} className="text-brand-gold flex-shrink-0" />
                      <span className="text-brand-bordo font-bold text-sm">{k.avantaj}</span>
                    </div>
                  ) : null}

                  {k.aciklama ? (
                    <p className="text-text-muted text-sm leading-relaxed mb-3 flex-1">{k.aciklama}</p>
                  ) : (
                    <div className="flex-1" />
                  )}

                  <div className="space-y-1.5 pt-3 border-t border-border-light">
                    {k.adres ? (
                      <div className="flex items-center gap-2 text-xs text-text-soft">
                        <MapPin size={13} className="flex-shrink-0" />
                        {k.adres}
                      </div>
                    ) : null}
                    {k.telefon ? (
                      <a
                        href={`tel:${k.telefon}`}
                        className="flex items-center gap-2 text-xs text-text-soft hover:text-brand-bordo transition-colors"
                      >
                        <Phone size={13} className="flex-shrink-0" />
                        {k.telefon}
                      </a>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnlasmaliKurumlarPage;
